import React, { useEffect, useState } from "react";
import { DollarSign, Check, X as XIcon } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const DEFAULT_PRICE = 0.14;

/**
 * HomePriceOnboarding — one-time modal that asks new owners for their
 * home $/kWh so the Charging ledger $/mile lights up on the very first
 * session. Uses per-VIN battery_history to remember the answer + the
 * onboarding_complete flag so it never shows twice.
 */
export const HomePriceOnboarding = ({ vehicle }) => {
  const vin = vehicle?.vin || null;
  const [show, setShow] = useState(false);
  const [price, setPrice] = useState(String(DEFAULT_PRICE));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!vin || !API_BASE) return;
    // Check flag once per VIN mount; skip if already onboarded.
    fetch(`${API_BASE}/api/battery_history/${encodeURIComponent(vin)}`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => {
        if (cancelled) return;
        if (!d.onboarding_complete) {
          setShow(true);
          if (typeof d.home_price_per_kwh_usd === "number") {
            setPrice(String(d.home_price_per_kwh_usd));
          }
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [vin]);

  const save = async () => {
    if (!vin) return;
    setSaving(true);
    const n = parseFloat(price);
    try {
      await fetch(`${API_BASE}/api/battery_history/${encodeURIComponent(vin)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          home_price_per_kwh_usd: isNaN(n) ? DEFAULT_PRICE : n,
          onboarding_complete: true,
        }),
      });
      setShow(false);
    } catch (_e) {
      setShow(false);
    } finally {
      setSaving(false);
    }
  };

  const skip = async () => {
    if (!vin) { setShow(false); return; }
    setSaving(true);
    try {
      await fetch(`${API_BASE}/api/battery_history/${encodeURIComponent(vin)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ onboarding_complete: true }),
      });
    } catch (_e) {}
    finally { setShow(false); setSaving(false); }
  };

  if (!show) return null;

  return (
    <div
      className="absolute inset-0 z-40 flex items-center justify-center px-3"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }}
      data-testid="home-price-onboarding"
    >
      <div
        className="rounded-2xl border p-4 max-w-[92%] w-full"
        style={{ borderColor: "rgba(255,176,32,0.4)", background: "linear-gradient(135deg, rgba(255,176,32,0.08), rgba(0,0,0,0.9))" }}
      >
        <div className="flex items-start gap-3 mb-3">
          <div
            className="w-10 h-10 rounded-md flex items-center justify-center border shrink-0"
            style={{ background: "rgba(255,176,32,0.14)", borderColor: "rgba(255,176,32,0.5)" }}
          >
            <DollarSign className="w-5 h-5" style={{ color: "#FFB020" }} strokeWidth={2.4} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
              One-time setup
            </div>
            <div className="text-sm text-white font-semibold leading-tight">
              What do you pay per kWh at home?
            </div>
            <div className="text-[11px] text-zinc-400 leading-snug mt-1">
              We'll use this to price charging sessions automatically so
              your $/mile lights up on the very first plug-in. US average
              is $0.14.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 mb-3 pl-1">
          <span className="font-mono text-[11px] text-zinc-500">$</span>
          <input
            type="number"
            step="0.001"
            min="0"
            max="2"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            data-testid="home-price-input"
            className="flex-1 rounded-md border border-white/10 bg-black/60 px-2 py-1.5 text-[13px] text-white font-mono focus:outline-none focus:border-[#FFB020]/60 focus:ring-1 focus:ring-[#FFB020]/40"
          />
          <span className="font-mono text-[11px] text-zinc-500">/ kWh</span>
        </div>
        <div className="flex items-center gap-2 justify-end">
          <button
            type="button"
            onClick={skip}
            disabled={saving}
            data-testid="home-price-skip"
            className="inline-flex items-center gap-1 rounded-md border border-white/10 px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-wider text-zinc-300 hover:text-white transition-colors"
          >
            <XIcon className="w-3 h-3" strokeWidth={2.4} />
            Skip
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="home-price-save"
            className="inline-flex items-center gap-1 rounded-md brand-gradient text-black px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider font-semibold hover:opacity-90 disabled:opacity-40"
          >
            <Check className="w-3 h-3" strokeWidth={2.6} />
            Save
          </button>
        </div>
      </div>
    </div>
  );
};
